import { can } from '../core/access';
import { esc, openModal, closeModal, onModalClose } from '../ui/modal';
import { LOG_ICON, REGISTRY_ICON } from './clicker-shared';
import { openClickerOperationsWindow } from './clicker-operations-modal';
import { t } from '../ui/lang';
/**
 * Read-only modal with the launch parameters a device's clicker is using, opened
 * from the running indicator on the farm device card. Admins also get a "Logs"
 * button. The config endpoint is authorized server-side, so operators only see it
 * for devices within their visibility.
 */
export async function showClickerInfoModal(udid, displayName, farmName) {
    const modal = openModal(`
        <h2 class="modal__title">${t('clicker.info_title', { device: esc(displayName) })}</h2>
        <div class="clicker-info-body"><p class="settings-hint">${t('common.loading')}</p></div>
        <div class="modal__actions clicker-info-actions"></div>
    `);
    const bodyEl = modal.querySelector('.clicker-info-body');
    const actionsEl = modal.querySelector('.clicker-info-actions');
    // The device card carries only the farm name; resolve its id for the API.
    let farmId;
    try {
        const r = await fetch('/api/farms');
        if (r.ok)
            farmId = (await r.json()).find((f) => f.name === farmName)?.id;
    }
    catch { /* ignore */ }
    if (farmId == null) {
        bodyEl.innerHTML = `<p class="modal__error" style="display:block">${t('clicker.could_not_resolve_farm')}</p>`;
        return;
    }
    let cfg;
    let status;
    try {
        const [cr, sr] = await Promise.all([
            fetch(`/api/farms/${farmId}/clicker/devices`),
            fetch(`/api/farms/${farmId}/clicker/status`),
        ]);
        if (cr.ok)
            cfg = (await cr.json())[udid];
        if (sr.ok)
            status = (await sr.json())[udid];
    }
    catch { /* ignore */ }
    const running = status?.state === 'running';
    const row = (label, value) => `<div class="clicker-info-row"><span class="clicker-info-row__label">${esc(label)}</span><span class="clicker-info-row__value">${value}</span></div>`;
    const dash = '<span class="settings-hint">—</span>';
    const val = (s) => (s ? esc(s) : dash);
    const farmDefault = `<span class="settings-hint">${t('clicker.farm_default')}</span>`;
    bodyEl.innerHTML = `
        <div class="clicker-info-status ${running ? 'is-running' : ''}">${running ? t('clicker.running') : (status?.state ? esc(status.state) : t('clicker.state_stopped'))}</div>
        ${row(t('clicker.task_type'), val(cfg?.task_type))}
        ${row(t('clicker.pin'), val(cfg?.pin))}
        ${row(t('clicker.app_password'), val(cfg?.password))}
        ${row(t('clicker.bucket_code'), cfg?.bucket_code ? esc(cfg.bucket_code) : farmDefault)}
        ${row(t('clicker.operators_group'), cfg?.operators_group ? esc(cfg.operators_group) : farmDefault)}
        ${row(t('clicker.tg_chats_short'), cfg?.tg_chats ? esc(cfg.tg_chats) : farmDefault)}
        ${row(t('clicker.min_initial_amount'), cfg?.min_initial_amount != null ? esc(String(cfg.min_initial_amount)) : farmDefault)}
        ${row(t('clicker.max_initial_amount'), cfg?.max_initial_amount != null ? esc(String(cfg.max_initial_amount)) : farmDefault)}
        ${row(t('clicker.min_balance'), cfg?.deposit_min_balance != null ? esc(String(cfg.deposit_min_balance)) : farmDefault)}
        ${row(t('clicker.max_balance'), cfg?.deposit_max_balance != null ? esc(String(cfg.deposit_max_balance)) : farmDefault)}
        ${row(t('clicker.app'), cfg?.app_name ? esc(cfg.app_name) : `<span class="settings-hint">${t('clicker.auto_detect')}</span>`)}
    `;
    // Admin-only: jump straight to the live logs.
    if (can('device.clickerLog')) {
        const logBtn = document.createElement('button');
        logBtn.type = 'button';
        logBtn.className = 'modal__submit clicker-info-logs';
        logBtn.innerHTML = `${LOG_ICON}<span>${t('clicker.logs')}</span>`;
        logBtn.addEventListener('click', () => { closeModal(); openClickerLogWindow(udid); });
        actionsEl.insertBefore(logBtn, actionsEl.firstChild);
    }
    // Operations registry — visible to everyone (server authorizes by device scope).
    const opsBtn = document.createElement('button');
    opsBtn.type = 'button';
    opsBtn.className = 'modal__submit clicker-info-ops';
    opsBtn.innerHTML = `${REGISTRY_ICON}<span>${t('clicker.operations')}</span>`;
    opsBtn.addEventListener('click', () => { closeModal(); openClickerOperationsWindow(farmId, udid, displayName); });
    actionsEl.insertBefore(opsBtn, actionsEl.firstChild);
}
/**
 * Stream a device's clicker logs in a dedicated window. Live tail over a separate
 * WebSocket (off the control relay) with navigation: autoscroll, text filter,
 * clear and a line counter.
 */
/** `farmId` pins the owning farm — an AdsPower profile id can sit in two farms that share an account. */
export function openClickerLogWindow(udid, farmId) {
    // Clicker logs are admin-only (the relay log socket is admin-gated server-side).
    if (!can('device.clickerLog'))
        return;
    const modal = openModal(`
        <h2 class="modal__title">${t('clicker.logs_title', { udid: esc(udid) })}</h2>
        <div class="clicker-log-toolbar">
            <input class="settings-input clicker-log-filter" type="text" placeholder="${t('clicker.log_filter_placeholder')}" autocomplete="off" />
            <label class="clicker-log-follow"><input type="checkbox" class="clicker-log-autoscroll" checked /> ${t('clicker.autoscroll')}</label>
            <span class="clicker-log-count">${t('clicker.log_lines_other', { count: 0 })}</span>
            <button type="button" class="tbl-btn clicker-log-clear">${t('clicker.clear')}</button>
        </div>
        <pre class="shell-output clicker-log-output"></pre>
    `);
    modal.classList.add('modal--clicker-log');
    const outputEl = modal.querySelector('.clicker-log-output');
    const filterEl = modal.querySelector('.clicker-log-filter');
    const autoscrollEl = modal.querySelector('.clicker-log-autoscroll');
    const countEl = modal.querySelector('.clicker-log-count');
    const clearBtn = modal.querySelector('.clicker-log-clear');
    const MAX_LINES = 1000;
    let lines = [];
    const render = () => {
        const f = filterEl.value.trim().toLowerCase();
        const shown = f ? lines.filter((l) => l.toLowerCase().includes(f)) : lines;
        outputEl.textContent = shown.join('\n');
        countEl.textContent = shown.length === 1
            ? t('clicker.log_lines_one', { count: shown.length })
            : t('clicker.log_lines_other', { count: shown.length });
        if (autoscrollEl.checked)
            outputEl.scrollTop = outputEl.scrollHeight;
    };
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const farmParam = farmId !== undefined ? `&farm=${farmId}` : '';
    const ws = new WebSocket(`${proto}://${location.host}/?action=clicker-log&udid=${encodeURIComponent(udid)}${farmParam}`);
    ws.onmessage = (ev) => {
        const text = typeof ev.data === 'string' ? ev.data : '';
        if (!text)
            return;
        const incoming = text.split('\n');
        // Drop the trailing '' produced by a terminating newline.
        if (incoming.length && incoming[incoming.length - 1] === '')
            incoming.pop();
        lines.push(...incoming);
        if (lines.length > MAX_LINES)
            lines = lines.slice(lines.length - MAX_LINES);
        render();
    };
    ws.onclose = () => { lines.push('[disconnected]'); render(); };
    ws.onerror = () => { lines.push('[connection error]'); render(); };
    filterEl.addEventListener('input', render);
    clearBtn.addEventListener('click', () => { lines = []; render(); });
    const close = () => { try {
        ws.close();
    }
    catch { /* ignore */ } closeModal(); };
    onModalClose(modal, close);
}
